import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import {
  CvaFormGroupComponent,
  ResetOptions
} from '@liberty-financial/agp-shared';
import { GuidUtil } from '@liberty-financial/ng-core';

import { ContactReferrerArrangement } from '@features/contacts/models/referrer-arrangement';
import { isStandardBaseDefinitionRequired } from '@shared/models/schema-property-definition';
import { StandardSchemaUtil } from '@shared/utilities/standard-schema-util';

interface ReferrerArrangementForm {
  payeeName: FormControl<string | null>;
  upfront: FormGroup<{
    type: FormControl<ContactReferrerArrangement.UpfrontType | null>;
    amount: FormControl<number | null>;
    percent: FormControl<number | null>;
  }>;
  trail: FormControl<number | null>;
}

@Component({
  selector: 'spark-contacts-referrer-arrangement-form',
  templateUrl: './form.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactReferrerArrangementFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ContactReferrerArrangementFormComponent),
      multi: true
    }
  ]
})
export class ContactReferrerArrangementFormComponent
  extends CvaFormGroupComponent<ContactReferrerArrangement.Model>
  implements OnInit, OnChanges, OnDestroy
{
  @Input() identifier = GuidUtil.generate();
  @Input()
  schema!: ContactReferrerArrangement.SchemaPropertyDefinition;
  form = this.fb.group<ReferrerArrangementForm>({
    payeeName: this.fb.control<string | null>(null),
    upfront: this.fb.group({
      type: this.fb.control<ContactReferrerArrangement.UpfrontType | null>(
        null
      ),
      amount: this.fb.control<number | null>(null),
      percent: this.fb.control<number | null>(null)
    }),
    trail: this.fb.control<number | null>(null)
  });
  upfrontType = ContactReferrerArrangement.UpfrontType;

  constructor(private readonly fb: FormBuilder, cd: ChangeDetectorRef) {
    super(cd);
  }

  ngOnInit(): void {
    StandardSchemaUtil.attachFormGroupToSchema(this.form, this.schema);
    this.form.updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      isStandardBaseDefinitionRequired(this.schema.upfront.amount) ||
      isStandardBaseDefinitionRequired(this.schema.upfront.percent)
    ) {
      this.form.addValidators(Validators.required);
      this.form.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    StandardSchemaUtil.removeFormGroupFromSchema(this.form, this.schema);
    this.form.removeValidators(Validators.required);
    this.form.updateValueAndValidity();
    this.changeDetectorRef?.detectChanges();
  }

  protected reset(opts?: ResetOptions | undefined): void {
    this.form.reset(undefined, opts);
  }

  get isUpfrontPercent() {
    return (
      this.form.getRawValue().upfront.type ===
      ContactReferrerArrangement.UpfrontType.Percent
    );
  }
}
